import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTicketWebSocket } from '../hooks/useTicketWebSocket';

/**
 * RealtimeContext mantém UMA conexão WebSocket global e permite que componentes
 * (NotificationBell, TicketDetail, etc) se inscrevam para tipos específicos
 * de mensagem sem precisar abrir novas conexões.
 *
 * Uso:
 *   const { subscribe, status } = useRealtime();
 *   useEffect(() => subscribe('inbox_notification', (msg) => {...}), []);
 */
const RealtimeContext = createContext({
  status: 'closed',
  subscribe: () => () => {},
  subscribeTicket: () => {},
  unsubscribeTicket: () => {},
});

export const RealtimeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const listenersRef = useRef(new Map()); // type -> Set<callback>
  const [tickStatus, setTickStatus] = useState('closed');

  const handleMessage = useCallback((data) => {
    const type = data?.type;
    if (!type) return;
    const set = listenersRef.current.get(type);
    if (set) {
      set.forEach((cb) => {
        try { cb(data); } catch (e) { /* ignore */ }
      });
    }
  }, []);

  const { status, subscribe: wsSubscribe, unsubscribe: wsUnsubscribe } = useTicketWebSocket(isAuthenticated, handleMessage);

  // Sync external status state (RealtimeContext consumers don't need to call hook)
  React.useEffect(() => {
    setTickStatus(status);
  }, [status]);

  /**
   * Inscreve um listener para mensagens de um tipo específico.
   * Retorna função de unsubscribe.
   */
  const subscribe = useCallback((type, callback) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, new Set());
    }
    listenersRef.current.get(type).add(callback);
    return () => {
      const set = listenersRef.current.get(type);
      if (set) set.delete(callback);
    };
  }, []);

  const value = {
    status: tickStatus,
    subscribe,
    subscribeTicket: wsSubscribe,
    unsubscribeTicket: wsUnsubscribe,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);
